import type { AxiosError } from 'axios'
import { getEmbedSession } from 'v2/shared/utils/embed-session'
import { FlowTypes } from 'v2/types'
import { errorToast } from 'v2/utils/toasts'

// Embed mounts no console banners, so a Test agent that cannot start says why here.
export const embedTestErrorToast = (description: string) =>
  errorToast({ title: 'Unable to start the test', description })

// Embed has no profile to read a balance from; the runner still checks it before AI nodes.
export const shouldGateOnBalance = (prod: boolean, embed: boolean): boolean => prod && !embed

// Embed's PubNub grant covers a chat test's channel but no playground reply channel, so a reply
// without a chat channel (an SMS or WhatsApp conversation) cannot be sent from the embed.
export const canSendPlaygroundReply = (embed: boolean, chatChannel: string | undefined): boolean =>
  !embed || !!chatChannel

// Inbound voice only: the console shows no Test agent for outgoing_call either, and the text test
// needs routes the embed does not serve.
export const isEmbedTestableAgent = (agentType: string | undefined): boolean =>
  agentType === FlowTypes.incoming_call

// A 400 is core saying why it refused this agent (it forwards, transfers or dials out), so its
// text shows as sent. Anything else gets the embed's own copy.
export const requestErrorText = (status: number | undefined, serverErrors: unknown): string => {
  if (status === 400 && typeof serverErrors === 'string' && serverErrors.trim()) {
    return serverErrors.trim()
  }
  if (status === 403) {
    return "Test calls aren't available for your account. Ask your admin."
  }
  if (status === 429) {
    return 'Too many attempts. Try again in a moment.'
  }
  return "Couldn't set up the test call. Try again."
}

// A dead session already shows the expired overlay, so it gets no toast.
export const toastRequestError = (error: unknown): void => {
  if (!getEmbedSession()) {
    return
  }
  const response = (error as AxiosError<{ errors?: unknown }> | null)?.response
  embedTestErrorToast(requestErrorText(response?.status, response?.data?.errors))
}
